<template>
  <ModuleBase
    noBorder
    gray
    :loading="loadingReward"
  >
    <div
      :class="[
        $style.base,
        {
          [$style.smallScreen]: $screen === 's'
        }
      ]"
    >
      <BaseVisualBanner
        :heading="
          $t('referrals.heading', { amount: formattedAmount })
        "
        imagePath="/img/admin/referral.svg"
        v-test="'referrals-heading'"
      />
      <BaseCard :heading="`1. ${$t('referrals.step_1')}`">
        <Flex direction="column">
          <BaseCodeField
            :code="referralUrl"
            @copy="
              () => {
                if ($gtm) {
                  $gtm.trackEvent({ event: 'referral-copy-url' });
                }
              }
            "
            v-test="'referral-url'"
          />
          <div :class="$style.buttons">
            <BaseButton
              :href="mailHref"
              color="inverted"
              icon="email"
              @click="
                () => {
                  if ($gtm) {
                    $gtm.trackEvent({ event: 'referral-email' });
                  }
                }
              "
            >
              {{ $t('referrals.share_email') }}
            </BaseButton>
            <BaseButton
              :href="whatsappUrl"
              color="inverted"
              icon="whatsapp"
              @click="
                () => {
                  if ($gtm) {
                    $gtm.trackEvent({ event: 'referral-whatsapp' });
                  }
                }
              "
            >
              {{ $t('referrals.share_whatsapp') }}
            </BaseButton>
            <BaseButton
              :href="facebookUrl"
              color="inverted"
              icon="facebook"
              @click="
                () => {
                  if ($gtm) {
                    $gtm.trackEvent({ event: 'referral-facebook' });
                  }
                }
              "
            >
              {{ $t('referrals.share_facebook') }}
            </BaseButton>
          </div>
        </Flex>
      </BaseCard>
      <BaseCard>
        <BaseHeading>2. {{ $t('referrals.step_2', brandName()) }}</BaseHeading>
      </BaseCard>
      <BaseCard
        :heading="`3. ${$t('referrals.step_3', { amount: formattedAmount })}`"
      >
        <Flex direction="column">
          <BaseSpinner v-if="loadingReferrals" />
          <BaseText v-else>
            <span v-if="referrals.length">🎉</span>
            {{ $t(
                'referrals.signups_heading',
                { amount: referrals.length },
                referrals.length
              ) }}
          </BaseText>
          <BaseTable
            v-if="referrals.length"
            :headers="[
              $t('referrals.company_name'),
              $t('referrals.signed_up_on'),
              $t('referrals.rewarded')
            ]"
            :rows="
              referrals.map((referral) => ({
                cells: [
                  referral.companyName,
                  filters.date(referral.signupAt),
                  referral.rewarded
                ]
              }))
            "
            v-test="'referrals-list'"
          />
        </Flex>
      </BaseCard>
    </div>
  </ModuleBase>
</template>

<script lang="ts">
export default {
  name: 'Referrals',
};
</script>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import { brandName } from '@/helpers/brand-name';
import ModuleBase from '@/modules/ModuleBase.vue';
import { useCompanyStore } from '@/stores/company';
import type { Referral } from '@/types';
import { useReferralReward } from './useReferralReward';

const { t } = useI18n();
const { formattedAmount, loading: loadingReward } = useReferralReward();

const { result: referralsResult, loading: loadingReferrals } = useQuery(gql`
  query getReferrals {
    referrals {
      companyName
      rewarded
      signupAt
    }
  }
`);

const referrals = computed<Referral[]>(
  () => referralsResult.value?.referrals || [],
);

const { company } = useCompanyStore();
const referralUrl = `https://www.salonized.com/en/r/${company.publicId}`;
const mailHref = computed(() => {
  const subject = t('referrals.email_subject');
  const body = t('referrals.email_message', {
    link: referralUrl,
    amount: formattedAmount.value,
    ...brandName(),
  });

  return `mailto:?subject=${encodeURI(subject)}&body=${encodeURI(body)}`;
});

const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${referralUrl}`;
const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURI(`${t('referrals.message_whatsapp', brandName())} ${referralUrl}`)}`;
</script>

<style
  lang="scss"
  module
>
.base {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing * 0.5;
}
</style>
